export type CartMoney = {
  amount: number;
  currency: string; // ISO 4217 e.g., "EUR", "USD"
};

export type CartItem = {
  id: string;
  sku?: string;
  title: string;
  price: CartMoney;
  qty: number;
  thumbnail?: string;
  categoryId?: string;
  productRef?: any;
  variant?: Record<string, string>;
  notes?: string;
  maxQty?: number;
  // tax rate in percent (e.g., 5.5 means 5.5%) captured at add time
  taxRatePercent?: number;
};

export type CartState = {
  items: CartItem[];
  savedForLater: CartItem[];
};

export type AddItemAction = {
  type: "ADD_ITEM";
  payload: { item: CartItem; mergeById?: boolean };
};

export type RemoveItemAction = {
  type: "REMOVE_ITEM";
  payload: { id: string };
};

export type UpdateQtyAction = {
  type: "UPDATE_QTY";
  payload: { id: string; qty: number };
};

export type ClearCartAction = {
  type: "CLEAR_CART";
};


export type MoveToSavedAction = {
  type: "MOVE_TO_SAVED";
  payload: { id: string };
};

export type MoveToCartAction = {
  type: "MOVE_TO_CART";
  payload: { id: string };
};

export type SetNotesAction = {
  type: "SET_NOTES";
  payload: { id: string; notes: string };
};

export type LoadStateAction = {
  type: "LOAD_STATE";
  payload: CartState;
};

export type MergeCartAction = {
  type: "MERGE_CART";
  payload: { serverCart: CartState; policy?: "merge" | "replace" | "prefer_client" | "prefer_server" };
};

export type CartAction =
  | AddItemAction
  | RemoveItemAction
  | UpdateQtyAction
  | ClearCartAction
  | LoadStateAction
  | MoveToSavedAction
  | MoveToCartAction
  | SetNotesAction
  | MergeCartAction;

export function formatMoney(money: CartMoney, localeFromI18n: string): string {
  if (!Number.isFinite(money.amount)) return "";
  try {
    return new Intl.NumberFormat(localeFromI18n, {
      style: "currency",
      currency: money.currency || "EUR",
      currencyDisplay: "symbol",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(money.amount / 100);
  } catch {
    // Fallback if Intl fails or bad currency code
    return `${(money.amount / 100).toFixed(2)} ${money.currency || "EUR"}`;
  }
}

// Money math utilities to avoid floating point precision issues
export function addMoneyAmounts(a: number, b: number): number {
  // Convert to cents, add, then convert back to avoid floating point errors
  return Math.round((a + b) * 100) / 100;
}

export function multiplyMoneyAmount(
  amount: number,
  multiplier: number
): number {
  // Convert to cents, multiply, then convert back
  return Math.round(amount * multiplier * 100) / 100;
}

export function validateCurrency(currency: string): boolean {
  // Basic ISO 4217 currency code validation
  return /^[A-Z]{3}$/.test(currency);
}

export const initialCartState: CartState = {
  items: [],
  savedForLater: [],
};
